import axios, { AxiosResponse } from "axios";
import {
  EstimatedExchangeRequest,
  EstimatedExchangeResponse,
  CreateExchangeRequest,
  CreateExchangeResponse,
  ExchangeLimitsRequest,
  ExchangeLimitsResponse,
} from "@/types/bridge.types";
import { AGENTS_API_URL } from "@/config";

class BridgeApiService {
  // private baseURL = BRIDGE_API_URL;
  // private authToken = BRIDGE_API_TOKEN;

  private get axiosInstance() {
    return axios.create({
      baseURL: `${AGENTS_API_URL}/bridge`,
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 10000,
    });
  }

  /**
   * Get estimated exchange amount for a swap
   */
  async getEstimatedExchange(
    request: EstimatedExchangeRequest
  ): Promise<EstimatedExchangeResponse> {
    try {
      const response: AxiosResponse<EstimatedExchangeResponse> =
        await this.axiosInstance.post("/estimated-exchange", request);
      return response.data;
    } catch (error) {
      console.error("Bridge API Error - getEstimatedExchange:", error);
      throw new Error(
        `Failed to get estimated exchange: ${
          axios.isAxiosError(error) ? error.message : "Unknown error"
        }`
      );
    }
  }

  /**
   * Get estimated exchange amount with denomination support
   */
  async getEstimatedExchangeWithDenomination(
    request: EstimatedExchangeRequest
  ): Promise<EstimatedExchangeResponse> {
    try {
      const response: AxiosResponse<EstimatedExchangeResponse> =
        await this.axiosInstance.post(
          "/estimated-exchange?denomination=true",
          request
        );
      return response.data;
    } catch (error) {
      console.error(
        "Bridge API Error - getEstimatedExchangeWithDenomination:",
        error
      );
      throw new Error(
        `Failed to get estimated exchange with denomination: ${
          axios.isAxiosError(error) ? error.message : "Unknown error"
        }`
      );
    }
  }

  /**
   * Create a new exchange order
   */
  async createExchange(
    request: CreateExchangeRequest
  ): Promise<CreateExchangeResponse> {
    try {
      const response: AxiosResponse<CreateExchangeResponse> =
        await this.axiosInstance.post("/orders", request);
      return response.data;
    } catch (error) {
      console.error("Bridge API Error - createExchange:", error);
      throw new Error(
        `Failed to create exchange: ${
          axios.isAxiosError(error) ? error.message : "Unknown error"
        }`
      );
    }
  }

  /**
   * Get order info by ID
   */
  async getExchangeStatus(exchangeId: string): Promise<CreateExchangeResponse> {
    try {
      const response: AxiosResponse<CreateExchangeResponse> =
        await this.axiosInstance.get(`/orders/${exchangeId}`);
      return response.data;
    } catch (error) {
      console.error("Bridge API Error - getExchangeStatus:", error);
      throw new Error(
        `Failed to get exchange status: ${
          axios.isAxiosError(error) ? error.message : "Unknown error"
        }`
      );
    }
  }

  /**
   * Get exchange limits for asset pair
   */
  async getExchangeLimits(
    request: ExchangeLimitsRequest
  ): Promise<ExchangeLimitsResponse> {
    try {
      const response: AxiosResponse<ExchangeLimitsResponse> =
        await this.axiosInstance.post("/exchange-limits", request);
      return response.data;
    } catch (error) {
      console.error("Bridge API Error - getExchangeLimits:", error);
      throw new Error(
        `Failed to get exchange limits: ${
          axios.isAxiosError(error) ? error.message : "Unknown error"
        }`
      );
    }
  }

  /**
   * Get exchange limits with denomination support
   */
  async getExchangeLimitsWithDenomination(
    request: ExchangeLimitsRequest
  ): Promise<ExchangeLimitsResponse> {
    try {
      const response: AxiosResponse<ExchangeLimitsResponse> =
        await this.axiosInstance.post(
          "/exchange-limits?denomination=true",
          request
        );
      return response.data;
    } catch (error) {
      console.error(
        "Bridge API Error - getExchangeLimitsWithDenomination:",
        error
      );
      throw new Error(
        `Failed to get exchange limits with denomination: ${
          axios.isAxiosError(error) ? error.message : "Unknown error"
        }`
      );
    }
  }
}

export const bridgeApiService = new BridgeApiService();
